import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FlashOnIcon from '@mui/icons-material/FlashOnOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import { exportLeadsCsv, listLeads, bulkAssignLeads, bulkTransferLeads, bulkChangeLeadStatus } from '../../api/leads';
import { bulkSendWhatsApp } from '../../api/whatsapp';
import { listLookup } from '../../api/masterData';
import { AssignmentType, LeadListItem } from '../../types/lead';
import { downloadBlob } from '../../utils/downloadBlob';
import { useBranchFilter } from '../../context/branchContext';
import { useAuth } from '../../context/authContext';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';
import { TableToolbar } from '../../components/table/TableToolbar';
import { SavedFiltersBar } from '../../components/table/SavedFiltersBar';
import { PageHeader } from '../../components/PageHeader';
import { LeadRegistrationWizard } from './wizard/LeadRegistrationWizard';
import { BulkAssignDialog } from './BulkAssignDialog';
import { BulkWhatsAppDialog } from './BulkWhatsAppDialog';
import { BulkTransferDialog } from './BulkTransferDialog';
import { BulkStatusDialog } from './BulkStatusDialog';
import { ImportLeadsDialog } from './ImportLeadsDialog';
import { QuickLeadDialog } from './QuickLeadDialog';
import { GLOBAL_BRANCH_SCOPE_ROLES } from '../../utils/roleScope';
import { ApprovalStatus } from '../../types/lead';

const COUNSELOR_ROLE = 'Counselor';

const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const LeadsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { selectedBranchId } = useBranchFilter();
  const { user, hasPermission } = useAuth();
  // The backend already scopes the underlying query correctly for every
  // role (leadOwnerScopeWhere/branchScopeWhere in leads.service.ts) — this
  // is purely a label, matching Phase 6/7's "clearly labelled" ask, not a
  // second ownership filter. Super Admin/Director/Branch Manager etc. all
  // see "All Leads"; only a Counselor sees "My Leads".
  const isCounselor = user?.role.name === COUNSELOR_ROLE;
  const { counselors } = useCounselorOptions(!isCounselor);

  // Pending Leads by Counselor click-through (Counselor Dashboard §5/§19):
  // ?assignedToId=X&pending=true arrives once, on the initial navigation
  // here — read with a lazy initializer so it pre-fills these filters
  // without fighting the user's own later changes to them.
  const initialParams = useMemo(() => new URLSearchParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<number | ''>('');
  const [sourceId, setSourceId] = useState<number | ''>('');
  // Super Admin / non-Counselor "All Leads" extra filters (Phase 7) —
  // hidden for Counselors, who never need to filter by someone else's
  // ownership on their own already-owner-scoped view.
  const [assignedToId, setAssignedToId] = useState<number | ''>(() => {
    const v = initialParams.get('assignedToId');
    return v ? Number(v) : '';
  });
  const [assignmentStatus, setAssignmentStatus] = useState<'' | 'assigned' | 'unassigned'>('');
  const [assignmentType, setAssignmentType] = useState<AssignmentType | ''>('');
  // Pending Leads by Counselor / "My Pending Leads" — still requires
  // Counselor action: not terminal, approved, not yet converted to a
  // Student (see PENDING_LEAD_WHERE, backend/src/modules/leads/leads.service.ts).
  // Visible to every role (not just non-Counselor), since a Counselor's
  // own "My Pending Leads" KPI links here too.
  const [pendingOnly, setPendingOnly] = useState<'' | 'true' | 'false'>(() =>
    initialParams.get('pending') === 'true' ? 'true' : '',
  );
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | ''>('');
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [quickLeadOpen, setQuickLeadOpen] = useState(false);
  const [resumeLeadId, setResumeLeadId] = useState<number | null>(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);
  const [bulkWhatsAppOpen, setBulkWhatsAppOpen] = useState(false);
  const [bulkWhatsAppError, setBulkWhatsAppError] = useState<string | null>(null);
  const [bulkWhatsAppResult, setBulkWhatsAppResult] = useState<{
    total: number;
    sent: number;
    failed: number;
  } | null>(null);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTransferError, setBulkTransferError] = useState<string | null>(null);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusError, setBulkStatusError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const state = location.state as { quickCreate?: boolean; openQuickLead?: boolean } | null;
    // "+ New" menu bug fix: this must depend on location.state (not `[]`,
    // "run once on mount"). The "+ New" menu navigates to this same
    // /leads route via `navigate('/leads', {state})` — when the user is
    // already ON /leads, React Router does NOT unmount/remount this
    // component (same route match), it only updates location.state on
    // the *existing* instance. A mount-only effect would have already
    // fired (with whatever state existed at first mount, typically none)
    // and would never see this new state — which is exactly why "+ New"
    // items previously appeared to do nothing when clicked from this
    // page itself. Depending on location.state instead re-runs this
    // effect on every navigation, remount or not.
    if (state?.openQuickLead) {
      setQuickLeadOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    } else if (state?.quickCreate) {
      setResumeLeadId(null);
      setWizardOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filters = {
    search: search || undefined,
    leadStatusId: statusId || undefined,
    leadSourceId: sourceId || undefined,
    branchId: selectedBranchId,
    assignedToId: assignedToId || undefined,
    assignmentType: assignmentType || undefined,
    approvalStatus: approvalStatus || undefined,
    unassigned: assignmentStatus ? assignmentStatus === 'unassigned' : undefined,
    pending: pendingOnly ? pendingOnly === 'true' : undefined,
  };

  const leadsQuery = useQuery({
    queryKey: ['leads', paginationModel.page, paginationModel.pageSize, filters],
    queryFn: () =>
      listLeads({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        ...filters,
      }),
    placeholderData: (prev) => prev,
  });

  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
  });
  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources'),
  });

  const invalidateLeads = () => queryClient.invalidateQueries({ queryKey: ['leads'] });

  const bulkAssignMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason?: string }) =>
      bulkAssignLeads(selection as number[], userId, reason),
    onSuccess: () => {
      invalidateLeads();
      setBulkAssignOpen(false);
      setBulkAssignError(null);
      setSelection([]);
    },
    onError: (err) => setBulkAssignError(extractErrorMessage(err)),
  });

  const bulkWhatsAppMutation = useMutation({
    mutationFn: (input: { templateId?: number; body?: string }) =>
      bulkSendWhatsApp({ leadIds: selection as number[], ...input }),
    onSuccess: (result) => {
      setBulkWhatsAppResult(result);
      setBulkWhatsAppError(null);
    },
    onError: (err) => setBulkWhatsAppError(extractErrorMessage(err)),
  });

  const bulkTransferMutation = useMutation({
    mutationFn: ({ toBranchId, reason }: { toBranchId: number; reason?: string }) =>
      bulkTransferLeads(selection as number[], toBranchId, reason),
    onSuccess: () => {
      invalidateLeads();
      setBulkTransferOpen(false);
      setBulkTransferError(null);
      setSelection([]);
    },
    onError: (err) => setBulkTransferError(extractErrorMessage(err)),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: (leadStatusId: number) => bulkChangeLeadStatus(selection as number[], leadStatusId),
    onSuccess: () => {
      invalidateLeads();
      setBulkStatusOpen(false);
      setBulkStatusError(null);
      setSelection([]);
    },
    onError: (err) => setBulkStatusError(extractErrorMessage(err)),
  });

  const handleExport = async () => {
    const blob = await exportLeadsCsv(filters);
    downloadBlob(blob, `leads-export-${Date.now()}.csv`);
  };

  const columns: GridColDef<LeadListItem>[] = useMemo(
    () => [
      { field: 'leadNumber', headerName: 'Lead #', width: 110 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'leadSource',
        headerName: 'Source',
        width: 130,
        valueGetter: (_v, row) => row.leadSource.name,
      },
      {
        field: 'adIdName',
        headerName: 'Ad ID Name',
        width: 150,
        valueGetter: (_v, row) => row.adIdName ?? '',
      },
      {
        field: 'leadStatus',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.leadStatus.name}
            sx={{ bgcolor: params.row.leadStatus.colorHex, color: '#fff' }}
          />
        ),
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        width: 160,
        valueGetter: (_v, row) => (row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : '—'),
      },
      {
        field: 'branch',
        headerName: 'Branch',
        width: 130,
        valueGetter: (_v, row) => row.branch?.name ?? '—',
      },
      {
        field: 'approvalStatus',
        headerName: 'Approval',
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={APPROVAL_LABELS[params.row.approvalStatus]}
            color={
              params.row.approvalStatus === 'APPROVED'
                ? 'success'
                : params.row.approvalStatus === 'REJECTED'
                  ? 'error'
                  : 'warning'
            }
          />
        ),
      },
    ],
    [],
  );

  const isGlobalScope = !!user && GLOBAL_BRANCH_SCOPE_ROLES.includes(user.role.name);
  const pageTitle = isCounselor ? 'My Leads' : isGlobalScope ? 'All Leads' : 'Leads';
  const pageSubtitle = isCounselor
    ? 'Leads currently assigned to you.'
    : isGlobalScope
      ? 'Every lead across every branch and counselor.'
      : 'Track and manage inbound leads across all sources.';

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={ContactsIcon}
        color="#5B2DA3"
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <>
            {selection.length > 0 && hasPermission('leads', 'assign') && (
            <Button
              startIcon={<GroupIcon />}
              onClick={() => {
                setBulkAssignError(null);
                setBulkAssignOpen(true);
              }}
            >
              Assign ({selection.length})
            </Button>
          )}
          {selection.length > 0 && (
            <Button
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                setBulkWhatsAppError(null);
                setBulkWhatsAppResult(null);
                setBulkWhatsAppOpen(true);
              }}
            >
              WhatsApp ({selection.length})
            </Button>
          )}
          {selection.length > 0 && hasPermission('leads', 'transfer') && (
            <Button
              startIcon={<SwapHorizIcon />}
              onClick={() => {
                setBulkTransferError(null);
                setBulkTransferOpen(true);
              }}
            >
              Transfer ({selection.length})
            </Button>
          )}
          {selection.length > 0 && hasPermission('leads', 'update') && (
            <Button
              startIcon={<PlaylistAddCheckIcon />}
              onClick={() => {
                setBulkStatusError(null);
                setBulkStatusOpen(true);
              }}
            >
              Status ({selection.length})
            </Button>
          )}
          <Button startIcon={<DownloadIcon />} onClick={handleExport}>
            Export
          </Button>
          {hasPermission('leads', 'import') && (
            <Button startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
              Import
            </Button>
          )}
          {hasPermission('leads', 'create') && (
            <Button startIcon={<FlashOnIcon />} onClick={() => setQuickLeadOpen(true)}>
              Quick Lead
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setResumeLeadId(null);
              setWizardOpen(true);
            }}
          >
            New lead
          </Button>
          </>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search name, phone, email, lead #…"
          size="small"
          sx={{ width: 300 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={statusId}
          onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {statusesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Source"
          sx={{ width: 180 }}
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {sourcesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Counselor"
            sx={{ width: 180 }}
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {counselors.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </MenuItem>
            ))}
          </TextField>
        )}
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Assignment status"
            sx={{ width: 170 }}
            value={assignmentStatus}
            onChange={(e) => setAssignmentStatus(e.target.value as typeof assignmentStatus)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
          </TextField>
        )}
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Assignment type"
            sx={{ width: 190 }}
            value={assignmentType}
            onChange={(e) => setAssignmentType(e.target.value as typeof assignmentType)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
            <MenuItem value="COUNSELOR_CREATED">Counselor Created</MenuItem>
            <MenuItem value="CENTRAL_ASSIGNED">Central Assigned</MenuItem>
            <MenuItem value="TRANSFERRED">Transferred</MenuItem>
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Approval"
          sx={{ width: 170 }}
          value={approvalStatus}
          onChange={(e) => setApprovalStatus(e.target.value as typeof approvalStatus)}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Pending"
          sx={{ width: 150 }}
          value={pendingOnly}
          onChange={(e) => setPendingOnly(e.target.value as typeof pendingOnly)}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="true">Pending only</MenuItem>
          <MenuItem value="false">Not pending</MenuItem>
        </TextField>
        <SavedFiltersBar
          pageKey="leads"
          currentFilters={{ search, statusId, sourceId }}
          onApply={(f) => {
            setSearch(f.search);
            setStatusId(f.statusId);
            setSourceId(f.sourceId);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={leadsQuery.data?.data ?? []}
          columns={columns}
          rowCount={leadsQuery.data?.meta.total ?? 0}
          loading={leadsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          onRowClick={(params) => {
            if (params.row.leadStatus.name === 'Draft') {
              setResumeLeadId(params.row.id);
              setWizardOpen(true);
            } else {
              navigate(`/leads/${params.row.id}`);
            }
          }}
        />
      </Box>

      <LeadRegistrationWizard
        open={wizardOpen}
        resumeLeadId={resumeLeadId}
        onClose={() => setWizardOpen(false)}
        onSaved={invalidateLeads}
      />

      <QuickLeadDialog
        open={quickLeadOpen}
        onClose={() => setQuickLeadOpen(false)}
        onSaved={(lead) => {
          invalidateLeads();
          navigate(`/leads/${lead.id}`);
        }}
      />

      <BulkAssignDialog
        open={bulkAssignOpen}
        leadCount={selection.length}
        errorMessage={bulkAssignError}
        onClose={() => setBulkAssignOpen(false)}
        onSubmit={async (userId, reason) => {
          await bulkAssignMutation.mutateAsync({ userId, reason });
        }}
      />

      <BulkWhatsAppDialog
        open={bulkWhatsAppOpen}
        leadCount={selection.length}
        errorMessage={bulkWhatsAppError}
        resultSummary={bulkWhatsAppResult}
        onClose={() => setBulkWhatsAppOpen(false)}
        onSubmit={async (input) => {
          await bulkWhatsAppMutation.mutateAsync(input);
        }}
      />

      <BulkTransferDialog
        open={bulkTransferOpen}
        leadCount={selection.length}
        errorMessage={bulkTransferError}
        onClose={() => setBulkTransferOpen(false)}
        onSubmit={async (toBranchId, reason) => {
          await bulkTransferMutation.mutateAsync({ toBranchId, reason });
        }}
      />

      <BulkStatusDialog
        open={bulkStatusOpen}
        leadCount={selection.length}
        errorMessage={bulkStatusError}
        onClose={() => setBulkStatusOpen(false)}
        onSubmit={async (leadStatusId) => {
          await bulkStatusMutation.mutateAsync(leadStatusId);
        }}
      />

      <ImportLeadsDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={invalidateLeads} />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
